import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useNotification } from "../../notifications/Notifications";
import { addRecipient, deleteRecipient, fetchRecipients } from "../../services/recipientService";
import { useSelector, useDispatch } from "react-redux";
import { getTranslation } from "../../translations/getTranslations";
import { updateGateway } from "../../redux/slices/gatewaysSlice";

const RecipientManagerModal = ({ gatewayId, showModal, onClose }) => {
  const [recipients, setRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState("");
  const language = useSelector(state => state.settings.language);
  const gateways = useSelector(state => state.gateways.data);
  const dispatch = useDispatch();

  const translation = key => getTranslation("recipientManager", key, language);

  const notify = useNotification();

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const handleAddRecipient = async () => {
    const emails = newRecipient
      .split(",")
      .map(email => email.trim())
      .filter(email => email !== "");

    // Validate each email in the list
    const invalidEmails = emails.filter(email => !emailRegex.test(email));
    if (invalidEmails.length > 0) {
      notify("error", translation(`${translation("_invalidEmailError")}: ${invalidEmails.join(", ")}`));
      return;
    }

    if (emails.length === 0) return;

    const existingEmails = new Set(recipients.map(recipient => recipient.email));

    const newEmails = emails.filter(email => !existingEmails.has(email));

    if (newEmails.length === 0) {
      notify("error", translation(translation("_duplicateEmailError")));
      return;
    }

    const success = await addRecipient(gatewayId, newEmails);
    if (success) {
      const recipients = await fetchRecipients(gatewayId);

      dispatch(
        updateGateway({
          id: gatewayId,
          updates: {
            recipients: recipients,
          },
        }),
      );

      setNewRecipient("");
      notify("success", translation("_recipientAdded"));
    } else {
      notify("error", translation("_addRecipientError"));
    }
  };

  const handleDeleteRecipient = async recipientId => {
    try {
      const resultKey = await deleteRecipient(gatewayId, recipientId);
      const recipients = await fetchRecipients(gatewayId);
      dispatch(
        updateGateway({
          id: gatewayId,
          updates: { recipients },
        }),
      );
      const translationKey = resultKey?.trim().replace(/^"+|"+$/g, "");
      notify("success", translation(translationKey));
    } catch (error) {
      const errorKey = error.message?.trim().replace(/^"+|"+$/g, "");
      notify("error", translation(errorKey));
    }
  };

  useEffect(() => {
    if (showModal) {
      const gateway = gateways.find(item => item.id === gatewayId);
      if (gateway && Array.isArray(gateway.recipients)) {
        setRecipients(gateway.recipients);
      } else {
        setRecipients([]); // fallback to empty array
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, gateways, gatewayId]);

  useEffect(() => {
    if (showModal) {
      setNewRecipient("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-gray-800 bg-opacity-50">
      <div className="relative w-[600px] rounded bg-backgroundPrimary p-6 text-center text-lg text-textPrimary shadow-lg">
        <button
          className="absolute right-1 top-0 p-2 text-xl text-buttonCancel hover:text-buttonCancelHover"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2 className="mb-6 text-2xl font-semibold">{translation("_recipientsManagement")}</h2>
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <input
              value={newRecipient}
              onChange={e => setNewRecipient(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleAddRecipient()}
              placeholder={translation("_addRecipientPlaceholder")}
              className="flex-grow rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none"
            />
            <button
              className="w-[20%] rounded bg-buttonSubmit p-1 text-buttonText hover:bg-buttonSubmitHover"
              onClick={handleAddRecipient}
            >
              {translation("_addRecipient")}
            </button>
          </div>

          <p className="text-sm text-gray-500">{translation("_multipleEmailsHint")}</p>
          <p className="h-5 text-sm text-red-500"></p>

          <ul className="max-h-60 space-y-2 overflow-y-auto">
            {recipients.length === 0 ? (
              <li>{translation("_noRecipientsFound")}</li>
            ) : (
              recipients.map(recipient => (
                <li key={recipient.id} className="flex items-center justify-between rounded bg-inputBackground p-2">
                  <span>{recipient.email}</span>
                  <button
                    className="flex p-2 text-buttonCancel hover:text-buttonCancelHover"
                    onClick={() => handleDeleteRecipient(recipient.id)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RecipientManagerModal;
